import { fetchClient } from "@/api/api";
import type {
	acknowledgeBannerInput,
	acknowledgeBannerOutput,
	createBannerInput,
	createBannerOutput,
	deleteBannerInput,
	deleteBannerOutput,
	getActiveBannersInput,
	getActiveBannersOutput,
	getGenericBannersOutput,
	updateBannerInput,
	updateBannerOutput,
} from "@/api/custom_types";
import { useApiError } from "@/hooks";
import { queryClient } from "@/utils/queryClient";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { BANNER_SERVICE_KEYS } from "./keys";

const useGetActiveBanners: CustomQueryHook<
	getActiveBannersOutput,
	getActiveBannersInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: BANNER_SERVICE_KEYS.useGetActiveBanners(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/v1/marketplace/banner/", {
				params: { query: payload?.query },
				throwOnError: true,
			});
			if (error) throw error;
			return data;
		},
	});
};

const useGetGenericBanners: CustomQueryHook<
	getGenericBannersOutput,
	undefined
> = (options) => {
	return useQuery({
		...options,
		queryKey: BANNER_SERVICE_KEYS.useGetGenericBanners(),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/banner/generic",
				{ throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
	});
};

const invalidateBannerLists = () => {
	queryClient.invalidateQueries({
		queryKey: BANNER_SERVICE_KEYS.useGetActiveBanners(),
	});
	queryClient.invalidateQueries({
		queryKey: BANNER_SERVICE_KEYS.useGetGenericBanners(),
	});
};

const useCreateBanner: CustomMutationHook<
	createBannerInput,
	createBannerOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: BANNER_SERVICE_KEYS.useCreateBanner(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/banner/",
				{ body: payload.body, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success("Banner created successfully");
			invalidateBannerLists();
		},
	});
};

const useUpdateBanner: CustomMutationHook<
	updateBannerInput,
	updateBannerOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: BANNER_SERVICE_KEYS.useUpdateBanner(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/marketplace/banner/{banner_id}",
				{
					params: { path: payload.path },
					body: payload.body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success("Banner updated successfully");
			invalidateBannerLists();
		},
	});
};

const useDeleteBanner: CustomMutationHook<
	deleteBannerInput,
	deleteBannerOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: BANNER_SERVICE_KEYS.useDeleteBanner(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.DELETE(
				"/v1/marketplace/banner/{banner_id}",
				{
					params: { path: payload.path },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success("Banner deleted successfully");
			invalidateBannerLists();
		},
	});
};

const useAcknowledgeBanner: CustomMutationHook<
	acknowledgeBannerInput,
	acknowledgeBannerOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: BANNER_SERVICE_KEYS.useAcknowledgeBanner(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/banner/{banner_id}/acknowledge",
				{
					params: {
						path: payload.path,
						query: payload.query,
					},
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: BANNER_SERVICE_KEYS.useGetActiveBanners(),
			});
		},
	});
};

export {
	useGetActiveBanners,
	useGetGenericBanners,
	useCreateBanner,
	useUpdateBanner,
	useDeleteBanner,
	useAcknowledgeBanner,
};
